import { useState } from 'react'
import type { ChecklistItemResponse, ChecklistSectionResponse } from '../api/types'
import { CHECKLIST_STATUSES, checklistStatusLabel } from '../lib/checklist'
import { ROLLUP_BUCKETS, checklistMatrix, type MatrixRow } from '../lib/checklistView'

/**
 * The cover sheet of the deal team's workbook (ADR 0009 s7): status by workstream, count and
 * percent per cell, a total row, and High priority reported on its own. It is built from the
 * list this screen already holds, so it cannot disagree with the rows beneath it. The four
 * buckets are the workbook's, so the numbers reconcile with the file the team kept until now;
 * the six states are behind the toggle.
 *
 * A bar is a <meter>: the CSP allows no inline style, so no computed width.
 */
export function ChecklistMatrix({
  sections,
  items,
  onPickSection,
}: {
  sections: ChecklistSectionResponse[]
  items: ChecklistItemResponse[]
  /** Narrow the list to one workstream ('none' for the items that have no section). */
  onPickSection: (sectionId: string) => void
}) {
  const [sixStates, setSixStates] = useState(false)
  if (items.length === 0) return null
  const matrix = checklistMatrix(sections, items)
  const headings = sixStates ? CHECKLIST_STATUSES.map(checklistStatusLabel) : ROLLUP_BUCKETS
  const cells = (row: MatrixRow) => (sixStates ? row.states : row.buckets)

  const line = (row: MatrixRow, total = false) => (
    <tr key={row.sectionId ?? row.name} className={total ? 'matrix-total' : undefined}>
      <th scope="row">
        {total ? (
          row.name
        ) : (
          <button
            type="button"
            className="link-btn"
            onClick={() => onPickSection(row.sectionId ?? 'none')}
          >
            {row.name}
          </button>
        )}
      </th>
      {cells(row).map((cell, i) => (
        <td key={headings[i]} className="mono">
          {cell.count}
          <span className="small muted"> {cell.percent}%</span>
        </td>
      ))}
      <td className="mono">{row.total}</td>
      <td>
        <meter
          className="matrix-meter"
          min={0}
          max={Math.max(row.total, 1)}
          value={row.answered}
          aria-label={`${row.name}: ${row.answered} of ${row.total} answered`}
        />
      </td>
      <td className={row.overdue > 0 ? 'mono overdue' : 'mono muted'}>{row.overdue}</td>
    </tr>
  )

  return (
    <details className="panel checklist-matrix" open>
      <summary className="checklist-section-summary">
        <span className="checklist-section-name">Progress by workstream</span>
        <span className="small muted">
          {matrix.total.answered} of {matrix.total.total} answered
          {matrix.high.total > 0 &&
            ` · High priority: ${matrix.high.answered} of ${matrix.high.total} answered`}
          {matrix.high.overdue > 0 && `, ${matrix.high.overdue} overdue`}
        </span>
      </summary>
      <table className="table matrix-table">
        <thead>
          <tr>
            <th scope="col">Workstream</th>
            {headings.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
            <th scope="col">Total</th>
            <th scope="col">Answered</th>
            <th scope="col">Overdue</th>
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((row) => line(row))}
          {line(matrix.total, true)}
        </tbody>
      </table>
      <label className="check small">
        <input
          type="checkbox"
          checked={sixStates}
          onChange={(e) => setSixStates(e.target.checked)}
        />
        <span>Show the six states instead of the workbook's four buckets</span>
      </label>
    </details>
  )
}
